import { Pagination, Space, Table, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import {
	DeleteActionSvg,
	EditActionSvg,
	EyeIcon,
	SortBottomIcon,
	SortTopIcon,
	TableNextIcon,
	TablePreviousIcon,
} from 'utils/svgs';
import { checkPermission } from 'utils/utils';
import { normalizeSaltComposition } from 'utils/helper';
import SearchAndFilter from '../SearchAndFilter';
import styles from './index.module.scss';
import './index.scss';
import { CustomUserTableProps } from './type';

const formatSaltCompositionCell = (value: unknown): string => {
	const salts = normalizeSaltComposition(value);
	if (!salts.length) return '';
	return salts
		.map((salt) => {
			const name = salt?.name != null ? String(salt.name).trim() : '';
			if (!name) return null;
			const strength = salt?.strength != null ? String(salt.strength).trim() : '';
			return strength ? `${name} (${strength})` : name;
		})
		.filter(Boolean)
		.join(', ');
};



const CustomTable: FC<CustomUserTableProps> = (props) => {
	const { dataSource, handleTableChange, onShowSizeChange, isLoading, search, totalRecords, performSearchHandler, performFilterHandler, filterValue, openDrawerHandler, setEditSelectedMedicine, showModal, currentPage, pageSize, setSearch, setTitle, allUsers, setSelectedUser, selectedUser, selectedDate, setSelectedDate, viewHandler, statusFilter, onStatusFilterChange, categoryFilter, onCategoryFilterChange, categories, saltCompositionFilter, onSaltCompositionFilterChange } = props;

	const medicinesPermissions = useSelector(
		(state: any) => state?.userProfile
	);

	const isEditPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Medicines',
		permission: ['edit'],
	});

	const isImageEditPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['edit'],
	});

	const canEdit = isEditPermission || isImageEditPermission;

	const isDeletePermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Medicines',
		permission: ['delete'],
	});

	const isViewPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Medicines',
		permission: ['view'],
	});


	const editDataHandler = (medicineObject: any) => {
		openDrawerHandler();
		setTitle('Edit Medicine');
		setEditSelectedMedicine(medicineObject);
	};

	// Delete user data handler
	const deleteDataHandler = (medicineObject: any) => {
		setEditSelectedMedicine(medicineObject);
		showModal();
	};

	const viewDataHandler = (medicineObject: any) => {
		viewHandler(medicineObject);
	};

	return (
		<>
			<div className={`${styles['body']} user-table`} >
				<SearchAndFilter
					performSearchHandler={performSearchHandler}
					searchValue={search}
					performFilterHandler={performFilterHandler}
					filterValue={filterValue}
					title={'Medicine List'}
					openDrawerHandler={openDrawerHandler}
					setSearch={setSearch}
					allUsers={allUsers}
					setSelectedUser={setSelectedUser}
					selectedUser={selectedUser}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					totalRecords={totalRecords}
					statusFilter={statusFilter}
					onStatusFilterChange={onStatusFilterChange}
					categoryFilter={categoryFilter}
					onCategoryFilterChange={onCategoryFilterChange}
					categories={categories}
					saltCompositionFilter={saltCompositionFilter}
					onSaltCompositionFilterChange={onSaltCompositionFilterChange}
				/>

				<Table
					rowKey={(record: any) => record?.id}
					dataSource={dataSource}
					pagination={false}
					loading={isLoading}
					onChange={handleTableChange}
					scroll={{ y: 'calc(100vh - 300px)', x: 1400 }}
				>
					<Column
						title={<p style={{ padding: '0 10px' }}>Medicine Name</p>}
						dataIndex="medicineName"
						key="medicineName"
						className="user-column bg-white"
						width={'15%'}
						sorter
						sortDirections={['ascend', 'descend', 'ascend']}
						render={(value: any) => (value ? value : '-')}
						sortIcon={({ sortOrder }) => {
							const isAscend = sortOrder === 'ascend';
							const isDescend = sortOrder === 'descend';

							return (
								<>
									<SortTopIcon fill={isAscend ? '#949494' : undefined} />
									<SortBottomIcon fill={isDescend ? '#949494' : undefined} />
								</>
							);
						}}
					/>
					<Column
						title="Manufacturer"
						dataIndex="manufacturer"
						key="manufacturer"
						className="bg-white"
						width={'10%'}
						render={(value: any) => (value ? value : '-')}
					/>


					<Column
						title="Brand"
						dataIndex="brandName"
						key="brandName"
						className="bg-white"
						width={'10%'}
						render={(value: any) => (value ? value : '-')}
					/>

					<Column
						title="Salt Composition"
						dataIndex="saltComposition"
						key="saltComposition"
						className="bg-white"
						width={200}
						ellipsis
						render={(value: any) => {
							const text = formatSaltCompositionCell(value);
							if (!text) return '-';
							return (
								<Tooltip title={text} placement="topLeft">
									<span
										style={{
											display: 'inline-block',
											maxWidth: '100%',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											verticalAlign: 'bottom',
										}}
									>
										{text}
									</span>
								</Tooltip>
							);
						}}
					/>

					<Column
						title="Mrp"
						dataIndex="mrp"
						key="mrp"
						className="bg-white"
						width={'10%'}
						render={(value: any) => (value ? value : '-')}
					/>

					<Column
						title="Marketed By"
						dataIndex="marketedBy"
						key="marketedBy"
						className="bg-white"
						width={'10%'}
						render={(value: any) => (value ? value : '-')}
					/>

					<Column
						title="Image"
						dataIndex="image"
						key="image"
						className="bg-white"
						width={'10%'}
						align="center"
						render={(value: any, record: any) => {
							const hasPrimaryImage = record?.medicinePrimaryImageUrl && record.medicinePrimaryImageUrl.trim() !== '';
							const hasSecondaryImages = record?.medicineSecondaryImageUrl && Array.isArray(record.medicineSecondaryImageUrl) && record.medicineSecondaryImageUrl.length > 0;
							
							if (hasPrimaryImage || hasSecondaryImages) {
								return (
									<CheckCircleOutlined 
										style={{ 
											fontSize: '20px', 
											color: '#52c41a' 
										}} 
									/>
								);
							}
							return (
								<CloseCircleOutlined 
									style={{ 
										fontSize: '20px', 
										color: '#ff4d4f' 
									}} 
								/>
							);
						}}
					/>

					<Column
						title="Quality Check"
						dataIndex="qualityCheck"
						key="qualityCheck"
						className="bg-white"
						width={'10%'}
						render={(value: any) => {
							if (!value) return '-';

							const normalized = value.toUpperCase();
							const statusClass =
								normalized === 'PENDING'
									? styles.status__pending
									: normalized === 'REJECTED'
										? styles.status__rejected
										: styles.status__approved;

							return (
								<span className={statusClass}>
									{value}
								</span>
							);
						}}
					/>

					<Column
						title="Approved By"
						dataIndex={['approvedByUser', 'fullName']}
						key="approvedBy"
						className="bg-white"
						width={'10%'}
						render={(value: any) => (value ? value : '-')}
					/>

					{(canEdit || isDeletePermission) && (
						<Column
							title="Action"
							dataIndex="action"
							key="action"
							className="bg-white"
							width={'15%'}
							render={(value, data: any) => {
								return (
									<Space size={20}>
										{
											isViewPermission  ? (
												<div
													className="cursor-pointer flex align-center justify-center  "
													onClick={() =>
														viewDataHandler(data)
													}
												>
													<EyeIcon />
												</div>
											) : (
												<div className=" cursor-not-allowed flex align-center justify-center  ">
													<EyeIcon />
												</div>
											)}


										{canEdit? (
											<div
												className="cursor-pointer flex align-center justify-center rounded-action-btn"
												onClick={() =>
													editDataHandler(data)
												}
											>
												<EditActionSvg />
											</div>
										) : (
											<div className=" cursor-not-allowed flex align-center justify-center rounded-action-btn">

												<EditActionSvg />
											</div>
										)}

										{
											isDeletePermission ?(
												<div
													className="cursor-pointer flex align-center justify-center rounded-action-btn "
													onClick={() =>
														deleteDataHandler(data)
													}
												>
													<DeleteActionSvg />
												</div>
											) : (
												<div className=" cursor-not-allowed flex align-center justify-center rounded-action-btn ">
													<DeleteActionSvg />
												</div>
											)}
									</Space>
								);
							}}
						/>
					)}
				</Table>

				<div className={styles['pagination__wrapper']}>
					<Pagination
						current={currentPage}
						pageSize={pageSize}
						showSizeChanger
						onChange={onShowSizeChange}
						total={totalRecords}
						itemRender={(page, type, originalElement) => {
							return (
								<div className={styles['pagination']}>
									{type === 'prev' ? (
										<button
											className={`${styles['pagination']} ${styles['pagination__button']}`}
										>
											<TablePreviousIcon />
											<p
												className={
													styles['pagination__previous']
												}
											>
												Previous
											</p>
										</button>
									) : type === 'next' ? (
										<>
											<button
												className={`${styles['pagination']} ${styles['pagination__button']}`}
											>
												<p
													className={
														styles['pagination__next']
													}
												>
													Next
												</p>
												<TableNextIcon />
											</button>
										</>
									) : (
										originalElement
									)}
								</div>
							);
						}}
					/>
				</div>
			</div>
		</>
	);
};


export default CustomTable;
